import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login } from '../services/auth';
import './Login.css';
import BrandLogo from './BrandLogo';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Handle success messages from navigation (e.g., after registration)
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }

    // Handle error messages from URL query parameter (e.g., after account deactivation)
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clear URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.state, location.search]); // Only run when location.state or search changes

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(''); // Clear success message when submitting
    setLoading(true);

    try {
      const response = await login(username, password);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Small delay to ensure state is set before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Redirect based on role
      if (response.user.role === 'ADMIN') {
        navigate('/admin/orders');
      } else {
        navigate('/orders/new');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Login fehlgeschlagen';
      setError(errorMessage);
      setLoading(false);
      // Ensure error stays visible - don't clear it automatically
    }
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <div className="container login-header-inner">
          <div className="login-logo">
            <Link to="/" aria-label="ELFHALBE — zur Startseite">
              <BrandLogo height={18} withIcon />
            </Link>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
            Zur Startseite
          </button>
        </div>
      </header>
      <div className="login-container">
        <div className="login-card">
          <h2>Anmelden</h2>
          <p className="login-subtitle">Händlerportal · by Optik Bucher</p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Benutzername</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={e => {
                  setUsername(e.target.value);
                  // Don't clear error when user types - let them see the error
                }}
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Passwort</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  // Don't clear error when user types - let them see the error
                }}
                required
                disabled={loading}
              />
            </div>
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div className="success-message" role="status">
                {success}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Anmelden...' : 'Anmelden'}
            </button>
            <div className="register-link">
              <Link to="/register">Noch kein Account? Hier registrieren</Link>
            </div>
            <div className="register-link" style={{ marginTop: '10px' }}>
              <Link to="/forgot-password">Passwort vergessen?</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
